import React from 'react';
import { connect } from 'react-redux';
import LoadingOverlay from './LoadingOverlay';
import { main } from '../state/actions';
import Login from './Login';
import App from './App';
import GLOBAL_STYLES from '../styles';
import { Credentials } from 'aws-sdk';
import NIFCOAuth from '../helpers/NIFCOAuth';
import * as CONSTANTS from '../config';


const mapStateToProps = state => ({
  credentials: state.main.credentials,
  lastActivity: state.main.lastActivity,
  credentialsExpirationTime: state.main.credentialsExpirationTime,
  loadingMessage: state.main.loadingMessage,
  loggedInUser: state.main.loggedInUser,
  colorScheme: state.main.colorScheme,
});

const mapDispatchToProps = dispatch => ({
  updateCredentials: payload => dispatch(main.updateCredentials(payload)),
  updateLastActivity: payload => dispatch(main.updateLastActivity(payload)),
  logout: () => dispatch(main.logout()),
  updateColorScheme: payload => dispatch(main.updateColorScheme(payload)),
  updateWindowDimensions: payload => dispatch(main.updateWindowDimensions(payload)),
  updateScrollbarWidth: payload => dispatch(main.updateScrollbarWidth(payload)),
});


class Auth extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingStoredCredentials: true,
    };
    this._NIFCOAuth = new NIFCOAuth(
      CONSTANTS.AUTH_API_KEY,
      CONSTANTS.APP_ID,
      CONSTANTS.AUTH_PRIMARY_USER_TYPE,
    );
    this._checkLastActivity = this._checkLastActivity.bind(this);
    this._loadStoredCredentials = this._loadStoredCredentials.bind(this);
    this._checkForReAuthentication = this._checkForReAuthentication.bind(this);
    this._refreshCredentials = this._refreshCredentials.bind(this);
    this._updateWindowDimensions = this._updateWindowDimensions.bind(this);
    this.logout = this.logout.bind(this);
  }
  
  componentDidMount() {
    // update dimensions
    this._updateWindowDimensions();
    this._updateScrollbarWidth();
    window.addEventListener('resize', this._updateWindowDimensions);
    
    // set page title
    document.title = `NIFCO | ${CONSTANTS.APP_DISPLAY_NAME}`;
    
    // set app color scheme
    const colorScheme = localStorage.getItem(`com.nifconet.${CONSTANTS.APP_ID}.colorScheme`);
    if(colorScheme == null) localStorage.setItem(`com.nifconet.${CONSTANTS.APP_ID}.colorScheme`, CONSTANTS.DEFAULT_COLOR_SCHEME);
    this.props.updateColorScheme(colorScheme != null ? colorScheme : CONSTANTS.DEFAULT_COLOR_SCHEME);
    
    // start last activity checker interval
    if(CONSTANTS.NO_ACTIVITY_TIMEOUT && !isNaN(parseInt(CONSTANTS.NO_ACTIVITY_TIMEOUT, 10))) {
      this._checkLastActivity = setInterval(this._checkLastActivity, 5000);
    }
    
    // check to see if credentials need to be refreshed
    if(CONSTANTS.RE_AUTHENTICATION_ENABLED) {
      this.credCheckInterval = setInterval(this._checkForReAuthentication, 60000);
    }
    
    // attempt to load stored credentials
    this._loadStoredCredentials();
  }
  
  componentWillUnmount() {
    // clear window listener
    window.removeEventListener('resize', this._updateWindowDimensions);
    // clear last activity checker interval
    if(this._checkLastActivity) clearInterval(this._checkLastActivity);
    // clear re-auth checker interval
    if(this.credCheckInterval) clearInterval(this.credCheckInterval);
  }
  
  _updateWindowDimensions = () => this.props.updateWindowDimensions({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  _updateScrollbarWidth = () => this.props.updateScrollbarWidth(getScrollBarWidth());
  
  _loadStoredCredentials = () => {
    try {
      // attempt to load credentials from async storage
      let storedCredentials = sessionStorage.getItem(`com.nifconet.${CONSTANTS.APP_ID}.credentials`);
      if(storedCredentials == null) return;
      storedCredentials = JSON.parse(storedCredentials);
      if(Date.parse(storedCredentials.credentials.Expiration) > (Date.now() + 120000)) {
        this._NIFCOAuth.userType = storedCredentials.userType;
        this.props.updateCredentials({
          credentials: new Credentials(storedCredentials.credentials.AccessKeyId, storedCredentials.credentials.SecretAccessKey, storedCredentials.credentials.SessionToken),
          credentialsExpirationTime: Date.parse(storedCredentials.credentials.Expiration),
          authorizedModules: storedCredentials.authorizedModules,
          loggedInUser: storedCredentials.user,
          userType: storedCredentials.userType,
        });
      }
    }
    catch(e) {
      console.error('_loadStoredCredentials error:', e);
    }
    finally {
      this.setState({ loadingStoredCredentials: false });
    }
  };
  
  _checkLastActivity = () => {
    if((this.props.lastActivity != null &&
      (this.props.lastActivity + parseInt(CONSTANTS.NO_ACTIVITY_TIMEOUT, 10)) < Date.now()) ||
      (this.props.credentialsExpirationTime != null &&
        (this.props.credentialsExpirationTime - 60000) < Date.now())) {
      this.logout();
    }
  };
  
  _checkForReAuthentication = () => {
    if(this.props.credentials && this.props.credentials.accessKeyId && this.props.credentials.secretAccessKey && (this.props.credentialsExpirationTime - Date.now()) < (2 * 60000))
      this._refreshCredentials();
  };
  
  _refreshCredentials = async () => {
    const { credentials, loggedInUser, updateCredentials } = this.props;
    try {
      // attempt to reauthenticate
      const authData = await this._NIFCOAuth.reauthenticateUser(loggedInUser, credentials.sessionToken);
      if(authData == null || authData.credentials == null) {
        this.logout();
        return;
      }
      
      // add aws credentials to local storage
      sessionStorage.setItem(`com.nifconet.${CONSTANTS.APP_ID}.credentials`, JSON.stringify({
        ...authData,
        user: loggedInUser,
        userType: this._NIFCOAuth.userType,
      }));
      
      // set credential variables in state
      updateCredentials({
        credentials: new Credentials(authData.credentials.AccessKeyId, authData.credentials.SecretAccessKey, authData.credentials.SessionToken),
        credentialsExpirationTime: Date.parse(authData.credentials.Expiration),
        authorizedModules: authData.authorizedModules,
      });
    }
    catch(e) {
      console.error('Error occurred in re-authentication process:', e);
      this.logout();
    }
  };
  
  logout = () => {
    // remove aws credentials from session storage
    sessionStorage.clear();
    this.props.logout();
  };
  
  render() {
    const {
      loadingMessage,
      colorScheme,
      credentials,
    } = this.props;
    const { loadingStoredCredentials } = this.state;
    
    let currentView = null;
    if(loadingStoredCredentials) currentView = null;
    else if(credentials == null) currentView = <Login NIFCOAuth={this._NIFCOAuth}/>;
    else currentView = <App logout={this.logout}/>;
    
    return (
      <div
        style={{
          ...styles.mainAppContainer,
          backgroundColor: GLOBAL_STYLES[colorScheme].bgColor,
          color: GLOBAL_STYLES[colorScheme].fgColor,
        }}
      >
        {currentView}
        {loadingMessage != null ? <LoadingOverlay message={loadingMessage}/> : null}
      </div>
    );
  }
}

const styles = {
  mainAppContainer: {
    minHeight: '100vh',
  },
};

const getScrollBarWidth = () => {
  // Create the measurement node
  const scrollDiv = document.createElement('div');
  scrollDiv.className = 'scrollbar-measure-temp-style';
  document.body.appendChild(scrollDiv);
  
  // Get the scrollbar width
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  
  // Delete the DIV
  document.body.removeChild(scrollDiv);
  return scrollbarWidth;
};

export default connect(mapStateToProps, mapDispatchToProps)(Auth);